import { useFormikContext } from 'formik';
import React, { FC, useEffect } from "react";
import { COMMAND_STATE } from '../../../constants/command-status';
import Commands from '../../../constants/commands';
import { EngineSubCmds } from '../../../constants/sub-commands';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import hardwareIcon from '../../../images/Hardware.svg';
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import { executeCommand } from '../../../services/executeCommandService';
import { getErrorMessage, showError } from '../../../services/utilities/ServiceError';
import { toast } from "react-toastify";
import Label from '../../label/label';
import OutsideClick from '../../outside-click/outside-click';
import "./engine-status-modal.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { useIcaWebsocketContext } from '../../../contexts/websocket/IcaWebsocketContext';

interface EngineState {
    setShowModal: (showModal) => void,
}

const EngineStatusModal: FC<EngineState> = ({ setShowModal }) => {
    const { translate } = useLocale();
    const { setFieldValue, values } = useFormikContext<any>();
    const icaWebSocket = useIcaWebsocketContext();

    const extractTime = (dateTimeStamp: string) => {
        let timeStamp = dateTimeStamp;

        if (dateTimeStamp && dateTimeStamp.includes('T')) {
            const indexOfT = dateTimeStamp.indexOf('T') + 1;
            timeStamp = dateTimeStamp.substring(indexOfT, (indexOfT + 8));
        }

        if (!timeStamp) {
            timeStamp = '--:--:--';
        }

        return timeStamp;
    }

    const handleChange = async () => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Engine.label,
                cmdSubType: EngineSubCmds.Is_Engine_Started,
                jsonResponse: ""
            }

            // The state we want to toggle to (enable / disable)
            const reqToChangeTo = !icaWebSocket.engineState.isStarted;

            // Check current state
            const response: any = await executeCommand(command);
            const state = response.status === COMMAND_STATE.ENGINE_STOPPED ? false : true;

            if (!state) {
                toast.success(translate.byKey(TranslationKeys.engineStarted))
            } else {
                toast.success(translate.byKey(TranslationKeys.engineStopped))
            }

            if (state && reqToChangeTo) {
                //Server is already running...
                setFieldValue("enabled", state);
            }
            else if (!state && !reqToChangeTo) {
                // Server is already stopped
                setFieldValue("enabled", state);
            }
            else {
                setFieldValue("enabled", !state);
                enableDisableEngine(!state);
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const enableDisableEngine = (isEnabled) => {
        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: isEnabled ? EngineSubCmds.Start_Sync : EngineSubCmds.Stop_Sync,
            jsonResponse: ""
        }

        executeCommand(command)
            .then(() => {

            })
            .catch((error) => {
                toast.error(getErrorMessage(error));
            })
    }

    return (
        <OutsideClick onClick={() => setShowModal(false)}>
            <div className="status-modal flex flex-col">
                <div style={{ height: '72%', padding: '10px' }} className="flex flex-row">

                    {/* Left: The dialog icon */}
                    <div style={{ flex: '10%' }}>
                        <div className="rounded-full h-8 w-8 flex items-center justify-center" style={{ backgroundColor: '#CCCCCC' }}>
                            <img src={hardwareIcon} alt="" className="w-4 h-4 p-1-5 " />
                        </div>
                    </div>

                    {/* Right: title & status */}
                    <div style={{ flex: '80%' }}>
                        <div className="flex flex-col flex-grow">
                            <div style={{ width: '100%' }}>
                                <span style={{ float: 'left' }} className="text-xl font-semibold">{translate.byKey(TranslationKeys.engineStatus)}</span>
                                <div className="statusFlag flex items-center justify-left" style={{ backgroundColor: (icaWebSocket.engineState.isStarted ? '#99BB32' : 'rgb(210, 34, 42)') }}>
                                    <FontAwesomeIcon style={{ height: '1rem' }} className='faIcon' icon={faPowerOff} />
                                </div>
                            </div>

                            <div style={{ width: '100%', marginTop: '16px' }}>
                                <div className="my-0 mb-1">
                                    <Label
                                        htmlFor="enabled"
                                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.state)}: </Label>
                                    <label
                                        className="text-sm my-1"
                                        style={{ color: (icaWebSocket.engineState.isStarted ? '#99BB32' : 'rgb(210, 34, 42)'), fontWeight: 'bold', marginLeft: '4px' }}>{icaWebSocket.engineState.isStarted ? translate.byKey(TranslationKeys.enabled) : translate.byKey(TranslationKeys.disabled)}</label>
                                </div>
                                <div className="my-0 mb-1">
                                    <Label
                                        htmlFor="enabled"
                                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.lastChanged)}: </Label>
                                    <label
                                        className="text-sm my-1"
                                        style={{ fontWeight: 'bold', marginLeft: '4px' }}>{extractTime(icaWebSocket.engineState.dateTimeStamp)}</label>
                                </div>
                                <div className="my-0 mb-1">
                                    <Label
                                        htmlFor="enabled"
                                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.changedBy)}: </Label>
                                    <label
                                        className="text-sm my-1"
                                        style={{ fontWeight: 'bold', marginLeft: '4px' }}>{icaWebSocket.engineState.user ? icaWebSocket.engineState.user : '--'}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'block' }}>
                    <div className="overflow-y-auto status-modal-btm-bar wrapper">
                        <button
                            type="button"
                            style={{ float: 'right' }}
                            className={icaWebSocket.engineState.isStarted ? "btn-red-rounded" : "btn-green-rounded"}
                            onClick={handleChange} id="columnSelectorButtonId">{icaWebSocket.engineState.isStarted ? translate.byKey(TranslationKeys.disable) : translate.byKey(TranslationKeys.enable)}
                        </button>
                    </div>
                </div>
            </div>


        </OutsideClick>
    )
}

export default EngineStatusModal;
